<script setup lang="ts">
import { decideProposal, getProposal, useMessaging, type Proposal } from "../composables/useMessaging"
import { t } from "../lib/i18n"

// A sales channel offer in a merchant conversation: its state now, and the answer for whoever may
// give it (the other shop's administrators accept or decline; the offering shop's may withdraw).
const props = defineProps<{ messageId: string; sending?: boolean; version: number }>()
const hub = useMessaging()
const proposal = ref<Proposal | null>(null)
const busy = ref(false)
const error = ref("")

async function load() {
  if (props.sending) return
  try {
    proposal.value = await getProposal(props.messageId)
  } catch {
    // Offline: the message's text says what was offered
  }
}
// Again when the conversation moves (an answer is a message too) and while Johku applies it
watch(() => [props.version, props.sending], () => void load(), { immediate: true })

const isAdminOf = (organisationId: string) => hub.me?.organisations.some((o) => o.id === organisationId && o.role === "admin") ?? false
const canAnswer = computed(() => proposal.value?.status === "pending" && isAdminOf(proposal.value.toOrganisationId))
const canWithdraw = computed(() => proposal.value?.status === "pending" && isAdminOf(proposal.value.fromOrganisationId))

const state = computed(() => {
  const p = proposal.value
  if (!p) return ""
  if (p.status === "pending") return t("Waiting for an answer")
  if (p.status === "declined") return t("Declined")
  if (p.status === "withdrawn") return t("Withdrawn")
  if (p.appliedAt) return t("Accepted, in effect in Johku")
  if (p.applyError) return t("Accepted, but Johku couldn't put it into effect: {error}", { error: p.applyError })
  return t("Accepted, being put into effect in Johku")
})

async function decide(decision: "accept" | "decline" | "withdraw") {
  busy.value = true
  error.value = ""
  try {
    proposal.value = await decideProposal(props.messageId, decision)
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="proposal" :class="proposal?.status">
    <div class="head">
      <HubIcon name="store" :size="18" />
      <strong>{{ proposal ? `${proposal.channelTitle} · ${proposal.commission} %` : t("Sales channel offer") }}</strong>
    </div>
    <p v-if="state" class="state">{{ state }}</p>
    <div v-if="canAnswer || canWithdraw" class="buttons">
      <template v-if="canAnswer">
        <button class="hub-btn" type="button" :disabled="busy" @click="decide('accept')">{{ t("Accept") }}</button>
        <button class="hub-btn hub-btn-quiet" type="button" :disabled="busy" @click="decide('decline')">{{ t("Decline") }}</button>
      </template>
      <button v-else class="hub-btn hub-btn-quiet" type="button" :disabled="busy" @click="decide('withdraw')">{{ t("Withdraw the offer") }}</button>
    </div>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.proposal {
  display: grid;
  gap: 6px;
  margin: 2px 0 6px;
  padding: 10px 12px;
  border: 1px solid var(--hub-border);
  border-radius: 12px;
  background: var(--hub-surface);
}

.head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.state {
  margin: 0;
  color: var(--hub-muted);
  font-size: 0.85rem;
}

.accepted .state {
  color: var(--hub-text);
}

.buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.buttons .hub-btn {
  min-height: 36px;
  padding: 6px 14px;
}

.error {
  margin: 0;
  color: var(--hub-danger);
  font-size: 0.85rem;
}

.hub-theme-pos .proposal {
  border-radius: 2px;
}
</style>
